import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../utils/auth';

interface Reservation {
  reservationId: string;
  amount: number;
  operationType: string;
  operationMetadata?: { extensionName?: string };
  createdAt: string;
}

interface Props {
  userId: string;
  onReleased: (totalCredits: number) => void;
}

export default function UserCreditReservations({ userId, onReleased }: Props) {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const mounted = useRef(false);
  const busy = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await apiRequest<{ reservations: Reservation[]; hasMore: boolean }>(
        `/api/admin/users/${encodeURIComponent(userId)}/credit-reservations`,
      );
      if (mounted.current) {
        setReservations(result.reservations);
        setHasMore(result.hasMore);
      }
    } catch (error) {
      if (mounted.current) setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to load reserved credits' });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
    // The parent keys this component by user ID to isolate in-flight actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const release = async (reservation: Reservation) => {
    if (busy.current) return;
    const reason = window.prompt(
      `Release ${reservation.amount} reserved credits?\nReservation: ${reservation.reservationId}\n\nThis returns the held credits and prevents this reservation from being charged later. This does not cancel a running operation. Its completion may fail because its credit reservation has been released.\n\nEnter a reason (required):`,
    );
    if (reason === null) return;
    if (!reason.trim() || reason.trim().length > 500) {
      setMessage({ type: 'error', text: 'Enter a reason containing 1–500 characters.' });
      return;
    }
    busy.current = true;
    setReleasing(reservation.reservationId);
    setMessage(null);
    try {
      const result = await apiRequest<{ totalCredits: number; alreadyReleased: boolean }>(
        `/api/admin/users/${encodeURIComponent(userId)}/credit-reservations/${encodeURIComponent(reservation.reservationId)}/release`,
        { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) },
      );
      if (!mounted.current) return;
      setReservations(current => current.filter(item => item.reservationId !== reservation.reservationId));
      onReleased(result.totalCredits);
      setMessage({ type: 'success', text: result.alreadyReleased ? 'Reservation was already released. Balance refreshed.' : 'Reserved credits released. Balance refreshed.' });
      await load();
    } catch (error) {
      if (mounted.current) setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to release reserved credits' });
    } finally {
      busy.current = false;
      if (mounted.current) setReleasing(null);
    }
  };

  return (
    <div className="um-card">
      <div className="um-card-header"><h3>Reserved Credits</h3></div>
      <div className="um-card-body">
        <p>Release a stuck hold to return its credits. Completed charges cannot be released here. Credits keep their original expiry date.</p>
        {message && <div className={`um-msg ${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</div>}
        <button className="um-btn-refresh" disabled={loading || releasing !== null} onClick={() => { setMessage(null); void load(); }}>Refresh reservations</button>
      </div>
      {loading ? <div className="um-empty">Loading reserved credits...</div> : reservations.length === 0 ? <div className="um-empty">No reserved credits</div> : (
        <div className="um-table-wrap"><table className="um-table">
          <thead><tr><th>Date</th><th>Reservation</th><th>Operation</th><th>Credits</th><th>Action</th></tr></thead>
          <tbody>{reservations.map(reservation => <tr key={reservation.reservationId}>
            <td>{new Date(reservation.createdAt).toLocaleString()}</td>
            <td className="um-txid">{reservation.reservationId}</td>
            <td>{reservation.operationMetadata?.extensionName || reservation.operationType}</td>
            <td>{reservation.amount}</td>
            <td><button className="um-btn-revoke" disabled={releasing !== null} onClick={() => void release(reservation)}>{releasing === reservation.reservationId ? 'Releasing…' : 'Release'}</button></td>
          </tr>)}</tbody>
        </table>{hasMore && <p>Showing the oldest 100 reservations. Release holds to load the next ones.</p>}</div>
      )}
    </div>
  );
}
